import React, { memo, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { IAs2Certificate, IAs2ConnectionConfig } from 'types/graphqlTypes'
import { EditIcon } from 'transfix-icons'
import { TableRow, TableCell, IconButton } from 'transfix-ui'
import { CONNECTION_CONFIG_TYPES } from '../ConnectionConfigTypes'
import { ValidityToolTip } from './AS2Certificates/ValidityToolTip'
import { certificateValidity } from './AS2Certificates/certificateValidity'
import { As2ConnectionConfigModal } from './modals/As2ConnectionConfigModal'
import { IAs2ConnectionConfigSchema } from './schema/AS2ConnectionConfigSchema'

interface IAS2ConnectionConfigTableRowProps {
  as2ConnectionConfig: IAs2ConnectionConfig
  onEdit: (options: IAs2ConnectionConfigSchema) => void
}

export const AS2ConnectionConfigTableRow = memo<IAS2ConnectionConfigTableRowProps>(
  ({ as2ConnectionConfig, onEdit }) => {
    const {
      as2Certificates,
      id,
      identifier,
      outboundUrl,
      serverInfoCredentialsIdentifier,
      transfixIdentifier,
    } = as2ConnectionConfig || {}
    const history = useHistory()
    const [enabledCertificate, setEnabledCertificate] = useState<IAs2Certificate>()
    const [openModal, setOpenModal] = useState<boolean>()
    const close = () => setOpenModal(false)

    const clickHandler = () => {
      history.push(`/connection-configs/as2/${id}`)
    }

    /**
     * Select an active certificate from certificates' list.
     * Only 1 certificate can be active at at time.
     */
    useEffect(() => {
      const certificate = as2Certificates.filter(item => item.isActive === true)
      if (certificate.length === 0) {
        setEnabledCertificate(undefined)
      } else {
        setEnabledCertificate(certificate[0])
      }
    }, [as2Certificates])

    /*
     * If Edit Modal is open, navigate back to /connection-configs to let the sidepanel stay closed
     */
    useEffect(() => {
      if (openModal) {
        history.push(`/connection-configs/${CONNECTION_CONFIG_TYPES.as2}`)
      }
    }, [history, openModal])

    const editClickHandler = () => {
      setOpenModal(true)
    }

    const { notAfter = null } = enabledCertificate || {}

    const currentValues = {
      id,
      identifier,
      outboundUrl,
      serverInfoCredentialsIdentifier,
      transfixIdentifier,
    }

    return (
      <TableRow dataTest={`as2-connection-config-${id}`} highlightOnHover onClick={clickHandler}>
        <TableCell>{id}</TableCell>
        <TableCell>{identifier}</TableCell>
        <TableCell>{outboundUrl}</TableCell>
        <TableCell>{serverInfoCredentialsIdentifier}</TableCell>
        <TableCell>{transfixIdentifier}</TableCell>
        <TableCell>
          {notAfter ? (
            <>
              {notAfter}
              <ValidityToolTip validity={certificateValidity(notAfter)} />
            </>
          ) : (
            'None of the certificates enabled'
          )}
        </TableCell>
        <TableCell>
          <IconButton
            Icon={EditIcon}
            color='tertiary'
            label='Edit AS2 Connection Config'
            onClick={editClickHandler}
            size='large'
          />
          {/* Adding modal here since <tbody> cannot have a <div> as a child */}
          <As2ConnectionConfigModal
            defaultValues={currentValues}
            open={openModal}
            onClose={close}
            onSubmit={onEdit}
            title='Edit AS2 Connection Config'
          />
        </TableCell>
      </TableRow>
    )
  }
)
